import { EChart } from '@kbox-labs/react-echarts';
import { useState } from 'react';

import { Close, Fullscreen, VDialog, VFlex, VSpacer, VStack } from '@/package';
import type { FrameGraphData, ObjectTypeData } from '@/type';

const ModelingGraph = ({
  graphDataList,
  selectedLabel,
}: {
  graphDataList: FrameGraphData[];
  selectedLabel: string[];
  toggleLabel: (label: string) => void;
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const sortedGraphDataList = graphDataList.sort(
    (a, b) => Number(a.distance) - Number(b.distance),
  );

  // NOTES: 디트로이트에서 탈것들은 모두 vehicle 로 처리됨
  const labelColorMap = {
    // car: '#C4323B',
    // truck: '#53DCD9',
    pedestrian: '#F3EB65',
    cyclist: '#BC5CF5',
    // van: '#64F64A',
    // bus: '#F6AB39',
    vehicle: '#C4323B',
  };

  const createGraphs = (frameData: FrameGraphData, distanceKey: string) => {
    const selectedLabelsLower = selectedLabel.map(label => label.toLowerCase());

    // TODO: 하드코딩 제거
    for (const label of [
      'vehicle',
      'pedestrian',
      'cyclist',
    ] as (keyof FrameGraphData)[]) {
      (frameData[label] as ObjectTypeData).confident_score_list ??= [];
      (frameData[label] as ObjectTypeData).precision_list ??= [];
      (frameData[label] as ObjectTypeData).recall_list ??= [];
    }

    const genSeriesData = (
      callback: (objectData: ObjectTypeData) => {
        lineData: number[][];
        scatterData: number[][];
      },
    ) => {
      return selectedLabelsLower
        .filter(label => frameData[label as keyof FrameGraphData])
        .flatMap(label => {
          // TODO: 하드코딩 제거
          const { lineData, scatterData } = callback(
            frameData[label as keyof FrameGraphData] as ObjectTypeData,
          );

          return [
            {
              name: label,
              type: 'line' as const,
              data: lineData,
              smooth: true,
              symbol: 'none',
              lineStyle: { width: 2 },
            },
            {
              name: label,
              type: 'scatter' as const,
              data: scatterData,
              symbol: 'circle',
              symbolSize: 8,
              showInLegend: false,
            },
          ];
        });
    };

    const prCurveData = genSeriesData(objectData => {
      const precisionRecallList = objectData
        .precision_list!.map((precision, index) => [
          objectData.recall_list![index],
          precision,
        ])
        .sort((a, b) => {
          if (a[0] === b[0]) {
            return b[1] - a[1];
          }

          return a[0] - b[0];
        });

      const recallSet = new Set<number>();
      const sortedLineData: [number, number][] = [];
      precisionRecallList.forEach(([recall, precision]) => {
        if (!recallSet.has(recall)) {
          recallSet.add(recall);
          sortedLineData.push([recall, precision]);
        }
      });

      const lineData = [...sortedLineData].filter(
        data => !!data[0] && !!data[1],
      );

      if (lineData.length > 1 && lineData[0][0]! > 0.01) {
        lineData.unshift([0.01, lineData[0][1]!]);
      }

      if (
        lineData.length > 1 &&
        lineData[lineData.length - 1].at(-1)! >= 0.01
      ) {
        lineData.push([0.99, 0.01]);
      }

      return {
        lineData,
        scatterData: [[objectData.max_f1_recall, objectData.max_f1_precision]],
      };
    });

    const recallConfidentData = genSeriesData(objectData => {
      const confidentRecallList = objectData
        .confident_score_list!.map((score, index) => [
          score,
          objectData.recall_list![index],
        ])
        .sort((a, b) => a[0] - b[0]);

      const recallSet = new Set<number>();
      const sortedLineData: [number, number][] = [];

      confidentRecallList.forEach(([recall, score]) => {
        if (!recallSet.has(recall)) {
          recallSet.add(recall);
          sortedLineData.push([recall, score]);
        }
      });

      const lineData = [...sortedLineData].filter(
        data => !!data[0] && !!data[1],
      );

      if (lineData.length > 1 && lineData[0][0]! > 0.01) {
        lineData.unshift([0.01, lineData[0][1]!]);
      }

      if (
        lineData.length > 1 &&
        lineData[lineData.length - 1].at(-1)! >= 0.01
      ) {
        lineData.push([0.99, 0.01]);
      }

      return {
        lineData,
        scatterData: [[objectData.target_score, objectData.max_f1_recall]],
      };
    });

    const precisionConfidentData = genSeriesData(objectData => {
      const confidentPrecisionList = objectData
        .confident_score_list!.map((score, index) => [
          score,
          objectData.precision_list![index],
        ])
        .sort((a, b) => a[0] - b[0]);

      const recallSet = new Set<number>();
      const sortedLineData: [number, number][] = [];

      confidentPrecisionList.forEach(([recall, score]) => {
        if (!recallSet.has(recall)) {
          recallSet.add(recall);
          sortedLineData.push([recall, score]);
        }
      });

      const lineData = [...sortedLineData].filter(
        data => !!data[0] && !!data[1],
      );

      if (lineData.length > 1 && lineData[0][0]! > 0.01) {
        lineData.unshift([0.01, 0.01]);
      }

      if (
        lineData.length > 1 &&
        lineData[lineData.length - 1].at(-1)! <= 0.99
      ) {
        lineData.push([0.99, 0.99]);
      }

      return {
        lineData,
        scatterData: [[objectData.target_score, objectData.max_f1_precision]],
      };
    });

    const graphs = [
      {
        title: 'PR Curve',
        seriesData: prCurveData,
        xAxis: { name: 'Recall', min: 0.0, max: 1.0 },
        yAxis: { name: 'Precision', min: 0.0, max: 1.0 },
      },
      {
        title: 'Recall / Confidence Score',
        seriesData: recallConfidentData,
        xAxis: { name: 'Confidence Score', min: 0.0, max: 1.0 },
        yAxis: { name: 'Recall', min: 0.0, max: 1.0 },
      },
      {
        title: 'Precision / Confidence Score',
        seriesData: precisionConfidentData,
        xAxis: { name: 'Confidence Score', min: 0.0, max: 1.0 },
        yAxis: { name: 'Precision', min: 0.0, max: 1.0 },
      },
    ];

    return graphs.map((graph, index) => (
      <div
        key={`${distanceKey}-${graph.title}-${index}`}
        className="h-full min-h-[300px] w-full bg-white"
      >
        <EChart
          key={`${distanceKey}-${graph.title}-${selectedLabel.join('-')}`}
          style={{ width: '100%', height: '100%' }}
          series={graph.seriesData}
          color={selectedLabel.map(key =>
            key in labelColorMap
              ? labelColorMap[key as keyof typeof labelColorMap]
              : '#000000',
          )}
          xAxis={{
            type: 'value',
            name: graph.xAxis.name,
            min: graph.xAxis.min,
            max: graph.xAxis.max,
            nameLocation: 'middle',
            nameGap: 30,
          }}
          yAxis={{
            type: 'value',
            name: graph.yAxis.name,
            min: graph.yAxis.min,
            max: graph.yAxis.max,
            nameLocation: 'middle',
            nameGap: 40,
          }}
          title={{
            text: graph.title,
            left: 'center',
            top: 20,
            textStyle: { fontSize: 14 },
          }}
          legend={{
            show: true,
            top: 50,
            right: 10,
            orient: 'vertical',
            itemWidth: 12,
            itemHeight: 12,
            icon: 'roundRect',
            formatter: () => '',
            tooltip: {
              show: true,
            },
          }}
          grid={{
            left: 60,
            right: 40,
            top: 70,
            bottom: 60,
          }}
          tooltip={{
            trigger: 'axis',
            formatter: (params: any) => {
              if (!params || params.length === 0) return '';

              const formatSeriesData = (param: any) => {
                if (
                  !param.data ||
                  !Array.isArray(param.data) ||
                  param.data.length <= 1
                )
                  return '';

                const color =
                  param.color ||
                  (param.seriesName in labelColorMap
                    ? labelColorMap[
                        param.seriesName as keyof typeof labelColorMap
                      ]
                    : '#000000');

                return `<span style="display:inline-block;margin-right:5px;border-radius:2px;width:10px;height:10px;background-color:${color}"></span>${param.seriesName} : ${Number(param.data[1]).toFixed(4)}`;
              };

              const axisValue = Array.isArray(params)
                ? params[0].axisValue
                : params.axisValue;
              let result = `<div style="font-size:14px;color:#fff;background-color:#2c3e50;padding:8px;border-radius:4px;">`;
              result += `<div style="font-weight:bold;margin-bottom:8px;">${graph.yAxis.name}</div>`;

              const paramsArray = Array.isArray(params) ? params : [params];

              const lineParams = paramsArray.filter(
                param => param.seriesType === 'line',
              );

              lineParams.forEach(param => {
                result += `<div>${formatSeriesData(param)}</div>`;
              });

              result += `<div style="margin-top:8px;border-top:1px solid #34495e;padding-top:8px;font-weight:bold;">`;
              const xAxisName =
                graph.xAxis.name === 'Confidence Score'
                  ? 'Confidence'
                  : graph.xAxis.name;
              result += `${xAxisName}: ${Number(axisValue).toFixed(4)}</div>`;
              result += `</div>`;

              return result;
            },
          }}
        />
      </div>
    ));
  };

  return (
    <>
      <div className="h-fit w-full @lg:row-span-2">
        <VStack className="h-full w-full">
          <VFlex justify="between" items="center">
            <VFlex items="center" gap={0.5}>
              <div className="text-headline-sm font-semibold">Graph</div>
              <VFlex items="center" gap={0.25}>
                {selectedLabel.map(label => {
                  return (
                    <div
                      key={label}
                      className="rounded-md bg-gray-200 px-4 py-1 text-body-lg font-semibold"
                    >
                      {label}
                    </div>
                  );
                })}
              </VFlex>
            </VFlex>

            <Fullscreen
              className="size-4"
              role="button"
              onClick={() => setIsModalOpen(true)}
            />
          </VFlex>

          <VSpacer size={2} />

          <VStack
            className="size-full rounded-md bg-Primary-Navy-Active p-4"
            gap={1}
          >
            {sortedGraphDataList.map(graphData => {
              const distance = graphData.distance;

              return (
                <VStack key={distance} className="w-full" gap={0.5}>
                  <div className="text-body-lg font-semibold">
                    Distance: +- {distance}m
                  </div>
                  <div className="grid grid-cols-1 gap-x-2 lg:grid-cols-3">
                    {createGraphs(graphData, distance.toString())}
                  </div>
                  <div className="h-px w-full bg-white" />
                </VStack>
              );
            })}
          </VStack>
        </VStack>
      </div>

      <VDialog
        visible={isModalOpen}
        onUpdateVisible={setIsModalOpen}
        dialogMainClass="w-[1280px]"
      >
        <VStack className="h-[832px] w-full bg-white p-6" gap={0.5}>
          <VStack className="size-full rounded-md" gap={1}>
            <VFlex justify="between" items="center">
              <VFlex items="center" gap={0.5}>
                <div className="text-headline-sm font-semibold">Graph</div>
                <VFlex items="center" gap={0.25}>
                  {selectedLabel.map(label => {
                    return (
                      <div
                        key={label}
                        className="rounded-md bg-gray-200 px-4 py-1 text-body-lg font-semibold"
                      >
                        {label}
                      </div>
                    );
                  })}
                </VFlex>
              </VFlex>

              <Close
                className="size-6 text-gray-400"
                role="button"
                onClick={() => setIsModalOpen(false)}
              />
            </VFlex>

            <div className="w-full overflow-y-auto bg-Primary-Navy-Active p-4">
              {sortedGraphDataList.map(graphData => {
                const distance = graphData.distance;

                return (
                  <VStack key={distance} className="w-full" gap={0.5}>
                    <div className="text-body-lg font-semibold">
                      Distance: +- {distance}m
                    </div>
                    <div className="grid grid-cols-1 gap-x-2 lg:grid-cols-3">
                      {createGraphs(graphData, distance.toString())}
                    </div>
                    <div className="h-px w-full bg-white" />
                  </VStack>
                );
              })}
            </div>
          </VStack>
        </VStack>
      </VDialog>
    </>
  );
};

export default ModelingGraph;
